<template>
  <agent-default>
    <div class="notif-shell">

      <!-- ── Page Header ── -->
      <div class="notif-header-band">
        <div class="header-left">
          <div class="header-icon-wrap">
            <v-icon size="20" color="white">mdi-bell-outline</v-icon>
          </div>
          <div>
            <h2 class="page-title">Agent Notifications</h2>
            <p class="page-subtitle">Track the progress of your processing motor policies.</p>
          </div>
        </div>
        <button class="refresh-btn" @click="refreshNotifications" :disabled="loading">
          <v-icon size="16" color="#1E4276" :class="{ 'spin': loading }">mdi-refresh</v-icon>
          <span>Refresh</span>
        </button>
      </div>

      <!-- ── Filter Pills ── -->
      <div class="filter-row">
        <button
          v-for="f in filters"
          :key="f.key"
          class="filter-pill"
          :class="{ 'filter-pill--active': activeFilter === f.key }"
          @click="setActiveFilter(f.key)"
        >
          <v-icon size="12" :color="activeFilter === f.key ? 'white' : '#64748b'">{{ f.icon }}</v-icon>
          {{ f.label }}
          <span class="filter-count" v-if="f.key === 'all'">{{ notifications.length }}</span>
        </button>
      </div>

      <!-- ── Loading ── -->
      <div v-if="loading" class="loading-state">
        <v-progress-circular :size="44" color="#00AEEF" indeterminate width="3" />
        <p>Loading notifications…</p>
      </div>

      <!-- ── Notification List ── -->
      <div v-else-if="filteredNotifications.length > 0" class="notif-list">
        <div
          v-for="n in filteredNotifications"
          :key="n.id"
          class="notif-card"
          :class="{ 'notif-card--unread': !n.read }"
          @click="handleNotification(n)"
        >
          <!-- Unread indicator -->
          <div v-if="!n.read" class="unread-dot"></div>

          <!-- Icon -->
          <div class="notif-icon" :class="getIconClass(n.type)">
            <v-icon size="18" color="white">{{ getIcon(n.type) }}</v-icon>
          </div>

          <!-- Body -->
          <div class="notif-body">
            <div class="notif-meta">
              <span class="notif-type-badge" :class="getTypeBadgeClass(n.type)">
                {{ formatType(n.type) }}
              </span>
              <span class="notif-time">
                <v-icon size="11" color="#CBD5E1">mdi-clock-outline</v-icon>
                {{ formatDate(n.createdAt) }}
              </span>
            </div>

            <div class="notif-main">
              <div class="notif-info">
                <h4 class="notif-title">{{ n.title }}</h4>
                <p class="notif-message">{{ n.message }}</p>
                <div class="client-row" v-if="n.clientName !== 'N/A'">
                  <v-icon size="12" color="#64748b">mdi-account-outline</v-icon>
                  <span>{{ n.clientName }}</span>
                  <span class="reg-badge" v-if="n.registrationNo && n.registrationNo !== 'N/A'">{{ n.registrationNo }}</span>
                </div>
              </div>
              <div class="notif-status-side" v-if="n.quoteStep">
                <div class="step-badge" :class="getStepClass(n.quoteStep)">
                  {{ formatStep(n.quoteStep) }}
                </div>
              </div>
            </div>

            <div class="notif-footer">
              <div class="notif-quote-chip">
                <v-icon size="11" color="#00AEEF">mdi-file-document-outline</v-icon>
                Quote #{{ n.quoteId }}
              </div>
              <div class="notif-track-btn">
                Track <v-icon size="12" color="#00AEEF">mdi-arrow-right</v-icon>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Empty State ── -->
      <div v-else class="empty-state">
        <div class="empty-illustration">
          <div class="empty-rings">
            <div class="empty-ring empty-ring--1"></div>
            <div class="empty-ring empty-ring--2"></div>
            <div class="empty-ring empty-ring--3"></div>
          </div>
          <div class="empty-bell">
            <v-icon size="36" color="#00AEEF">mdi-bell-check-outline</v-icon>
          </div>
        </div>
        <p class="empty-title">All caught up!</p>
        <p class="empty-sub">New updates on your motor sales will appear here as your policies are processed.</p>
        <button class="empty-refresh-btn" @click="refreshNotifications">
          <v-icon size="14" color="#1E4276">mdi-refresh</v-icon>
          Check again
        </button>
      </div>

    </div>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import axios from 'axios';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentMotorNotifications',
  components: { AgentDefault },
  data() {
    return {
      loading: false,
      notifications: [],
      activeFilter: 'all',
      filters: [
        { key: 'all', label: 'All', icon: 'mdi-view-list' },
        { key: 'PAYMENT', label: 'Payments', icon: 'mdi-cash-check' },
        { key: 'INSPECTION', label: 'Inspections', icon: 'mdi-car-search' },
        { key: 'CERTIFICATE', label: 'Certificates', icon: 'mdi-file-certificate' },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    filteredNotifications() {
      if (this.activeFilter === 'all') return this.notifications;
      return this.notifications.filter((n) => n.type.includes(this.activeFilter));
    },
  },
  mounted() {
    this.fetchNotifications();
  },
  methods: {
    refreshNotifications() {
      trackAgentInteraction(this, 'notifications_refresh');
      this.fetchNotifications();
    },
    setActiveFilter(filterKey) {
      this.activeFilter = filterKey;
      trackAgentInteraction(this, 'notifications_filter_change', {
        filter: filterKey,
      });
    },
    async fetchNotifications() {
      if (!this.authenticatedUser?.email) return;
      this.loading = true;
      try {
        const { email } = this.authenticatedUser;
        const pin = this.authenticatedUser['custom:kra-pin'] || '';
        const url = `/customer/notification/agent/app/all/pages/${email}?pin=${pin}&productGroup=MOTOR&page=1&size=20`;
        const res = await axios.get(url, { _skipErrorRedirect: true });
        const parsed = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        const raw = parsed.data || [];

        // Deduplication Logic: Keep only the latest notification for each quoteId
        const seen = new Set();
        const deduplicated = [];
        raw.forEach((n) => {
          const qId = n.quoteId || n.referenceId;
          if (qId) {
            if (!seen.has(qId)) {
              seen.add(qId);
              deduplicated.push(n);
            }
          } else {
            deduplicated.push(n); // Keep notifications without IDs (generic updates)
          }
        });

        this.notifications = deduplicated.map((n) => ({
          id: n.notificationId,
          type: n.notificationEnum || 'GENERAL',
          title: n.notificationEnum ? n.notificationEnum.replace(/_/g, ' ') : 'Policy Update',
          message: n.message,
          clientName: n.clientName || 'N/A',
          registrationNo: n.registrationNo || 'N/A',
          quoteId: n.quoteId || n.referenceId || 'N/A',
          quoteStep: n.quoteStep || '',
          createdAt: n.dateCreated || n.createdAt || new Date().toISOString(),
          read: n.read || false,
        }));
      } catch (e) {
        console.error('Failed to fetch notifications', e);
        this.$notify.error('Could not load notifications');
      } finally {
        this.loading = false;
      }
    },
    handleNotification(notification) {
      trackAgentInteraction(this, 'notifications_open_item', {
        notification_type: notification?.type || 'unknown',
        quote_id: notification?.quoteId || 'unknown',
      });
      const updated = notification;
      updated.read = true;
      this.$router.push({ name: 'AgentMotorFinalization', params: { quoteId: updated.quoteId } });
    },
    getIcon(type) {
      const m = {
        PAYMENT_RECEIVED: 'mdi-cash-check',
        INSPECTION_PENDING: 'mdi-car-search',
        UW_APPROVED: 'mdi-shield-check',
        CERTIFICATE_READY: 'mdi-file-certificate',
      };
      return m[type] || 'mdi-bell';
    },
    getIconClass(type) {
      const m = {
        PAYMENT_RECEIVED: 'icon-green',
        INSPECTION_PENDING: 'icon-amber',
        UW_APPROVED: 'icon-blue',
        CERTIFICATE_READY: 'icon-navy',
      };
      return m[type] || 'icon-gray';
    },
    getTypeBadgeClass(type) {
      const m = {
        PAYMENT_RECEIVED: 'badge-green',
        INSPECTION_PENDING: 'badge-amber',
        UW_APPROVED: 'badge-blue',
        CERTIFICATE_READY: 'badge-navy',
      };
      return m[type] || 'badge-gray';
    },
    formatType(type) {
      if (!type) return 'General';
      return type.replace(/_/g, ' ');
    },
    formatStep(step) {
      const m = {
        QUOTE_CREATED: 'Awaiting Payment',
        PROVIDE_DETAILS: 'Draft',
        POLICY_PAYMENT: 'Processing',
        UW_REVIEW: 'UW Review',
        INSPECTION_PENDING: 'Inspection',
        CERTIFICATE_READY: 'Ready',
        POLICY_ISSUED: 'Issued',
      };
      return m[step] || step.replace(/_/g, ' ');
    },
    getStepClass(step) {
      const m = {
        QUOTE_CREATED: 'step-amber',
        PROVIDE_DETAILS: 'step-gray',
        POLICY_PAYMENT: 'step-blue',
        UW_REVIEW: 'step-navy',
        INSPECTION_PENDING: 'step-amber',
        CERTIFICATE_READY: 'step-green',
        POLICY_ISSUED: 'step-green',
      };
      return m[step] || 'step-gray';
    },
    formatDate(val) {
      return new Date(val).toLocaleString('en-KE', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>

/* ── SHELL ── */
.notif-shell {
  max-width: 780px;
  margin: 0 auto;
  padding: 24px 28px 40px;
  box-sizing: border-box;
}

/* ── HEADER BAND ── */
.notif-header-band {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #1E4276 0%, #163363 100%);
  border-radius: 16px;
  padding: 20px 22px;
  margin-bottom: 16px;
  box-shadow: 0 6px 24px rgba(30,66,118,0.28);
}
.header-left { display: flex; align-items: center; gap: 14px; }
.header-icon-wrap {
  width: 44px; height: 44px; border-radius: 12px;
  background: rgba(0,174,239,0.22);
  border: 1px solid rgba(0,174,239,0.3);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 0 16px rgba(0,174,239,0.15);
}
.page-title {
  font-size: 1.1rem; font-weight: 800; color: white;
  margin: 0 0 3px; letter-spacing: -0.2px;
}
.page-subtitle {
  font-size: 0.75rem; color: rgba(255,255,255,0.5); margin: 0;
}

.refresh-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 8px; padding: 8px 14px;
  color: white; font-size: 0.75rem; font-weight: 600;
  cursor: pointer; transition: background 0.2s;
}
.refresh-btn:hover:not(:disabled) { background: rgba(255,255,255,0.18); }
.refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── FILTER PILLS ── */
.filter-row {
  display: flex; gap: 8px; flex-wrap: wrap;
  margin-bottom: 18px;
}
.filter-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 14px; border-radius: 20px;
  font-size: 0.72rem; font-weight: 700;
  background: white; color: #64748b;
  border: 1.5px solid #E2E8F0;
  cursor: pointer; transition: all 0.18s;
}
.filter-pill:hover { border-color: #00AEEF; color: #00AEEF; }
.filter-pill--active {
  background: #1E4276; color: white;
  border-color: #1E4276;
  box-shadow: 0 3px 10px rgba(30,66,118,0.25);
}
.filter-count {
  background: rgba(255,255,255,0.2);
  border-radius: 20px; padding: 1px 7px;
  font-size: 0.63rem; font-weight: 800;
}
.filter-pill:not(.filter-pill--active) .filter-count {
  background: #F1F5F9; color: #1E4276;
}

/* ── LOADING ── */
.loading-state {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 60px 0; gap: 14px;
  color: #94A3B8; font-size: 0.82rem;
}

/* ── NOTIFICATION LIST ── */
.notif-list { display: flex; flex-direction: column; gap: 10px; }

.notif-card {
  display: flex; align-items: flex-start; gap: 14px;
  background: white;
  border: 1.5px solid #E8EDF5;
  border-radius: 14px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(30,66,118,0.05);
}
.notif-card:hover {
  border-color: #00AEEF;
  box-shadow: 0 6px 20px rgba(0,174,239,0.1);
  transform: translateY(-2px);
}
.notif-card--unread {
  border-left: 3px solid #00AEEF;
  background: linear-gradient(135deg, #f0f9ff 0%, white 40%);
}

/* Unread dot */
.unread-dot {
  position: absolute; top: 14px; right: 14px;
  width: 8px; height: 8px; border-radius: 50%;
  background: #00AEEF;
  box-shadow: 0 0 6px rgba(0,174,239,0.6);
}

/* ── NOTIFICATION ICON ── */
.notif-icon {
  width: 42px; height: 42px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.icon-green { background: linear-gradient(135deg, #10B981, #059669); box-shadow: 0 4px 10px rgba(16,185,129,0.3); }
.icon-amber { background: linear-gradient(135deg, #FFC107, #F59E0B); box-shadow: 0 4px 10px rgba(255,193,7,0.3); }
.icon-blue  { background: linear-gradient(135deg, #00AEEF, #0284C7); box-shadow: 0 4px 10px rgba(0,174,239,0.3); }
.icon-navy  { background: linear-gradient(135deg, #1E4276, #163363); box-shadow: 0 4px 10px rgba(30,66,118,0.3); }
.icon-gray  { background: linear-gradient(135deg, #94A3B8, #64748B); }

/* ── NOTIFICATION BODY ── */
.notif-body { flex: 1; min-width: 0; }

.notif-meta {
  display: flex; align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.notif-type-badge {
  font-size: 0.6rem; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.5px;
  padding: 2px 8px; border-radius: 20px;
}
.badge-green { background: #DCFCE7; color: #16A34A; }
.badge-amber { background: #FEF3C7; color: #D97706; }
.badge-blue  { background: #E0F4FD; color: #0077aa; }
.badge-navy  { background: #EEF2FF; color: #1E4276; }
.badge-gray  { background: #F1F5F9; color: #64748b; }

.notif-time {
  display: flex; align-items: center; gap: 3px;
  font-size: 0.7rem; color: #94A3B8;
}
.notif-title {
  font-size: 0.9rem; font-weight: 800; color: #1E4276;
  margin: 0 0 4px; text-transform: capitalize;
}
.notif-message {
  font-size: 0.78rem; color: #64748b;
  margin: 0 0 8px; line-height: 1.55;
}
.notif-main {
  display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;
}
.notif-info { flex: 1; min-width: 0; }
.client-row {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.7rem; font-weight: 700; color: #64748b;
  background: #f8fafc; padding: 4px 10px; border-radius: 6px;
  width: fit-content;
}
.reg-badge {
  background: #1E4276; color: white; padding: 1px 6px;
  border-radius: 4px; font-size: 0.65rem; margin-left: 4px;
}
.notif-status-side { flex-shrink: 0; }
.step-badge {
  font-size: 0.65rem; font-weight: 800; padding: 4px 10px;
  border-radius: 8px; text-transform: uppercase; letter-spacing: 0.3px;
}
.step-amber { background: #FFFBEB; color: #D97706; border: 1px solid #FEF3C7; }
.step-gray  { background: #F8FAF7; color: #64748B; border: 1px solid #E2E8F0; }
.step-blue  { background: #F0F9FF; color: #0284C7; border: 1px solid #E0F2FE; }
.step-navy  { background: #EEF2FF; color: #1E4276; border: 1px solid #E0E7FF; }
.step-green { background: #F0FDF4; color: #16A34A; border: 1px solid #DCFCE7; }
.notif-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 9px;
  border-top: 1px dashed #E2E8F0;
}
.notif-quote-chip {
  display: inline-flex; align-items: center; gap: 4px;
  background: #F0F9FF; border: 1px solid #BAE6FD;
  border-radius: 20px; padding: 3px 10px;
  font-size: 0.68rem; font-weight: 700; color: #0077aa;
}
.notif-track-btn {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 0.72rem; font-weight: 700; color: #00AEEF;
  cursor: pointer;
}

/* ── EMPTY STATE ── */
.empty-state {
  display: flex; flex-direction: column;
  align-items: center; padding: 64px 20px;
  text-align: center;
}

/* Animated rings */
.empty-illustration {
  position: relative;
  width: 110px; height: 110px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 24px;
}
.empty-rings { position: absolute; inset: 0; }
.empty-ring {
  position: absolute; border-radius: 50%;
  border: 1.5px solid rgba(0,174,239,0.15);
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  animation: ringPulse 2.4s ease-in-out infinite;
}
.empty-ring--1 { width: 56px;  height: 56px; animation-delay: 0s; }
.empty-ring--2 { width: 80px;  height: 80px; animation-delay: 0.4s; }
.empty-ring--3 { width: 108px; height: 108px; animation-delay: 0.8s; }
@keyframes ringPulse {
  0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
  50%       { opacity: 0.15; transform: translate(-50%, -50%) scale(1.06); }
}
.empty-bell {
  position: relative; z-index: 1;
  width: 56px; height: 56px; border-radius: 50%;
  background: linear-gradient(135deg, #EBF8FF, #F0F9FF);
  border: 1.5px solid rgba(0,174,239,0.2);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 16px rgba(0,174,239,0.12);
}
.empty-title {
  font-size: 1.2rem; font-weight: 800; color: #1E4276;
  margin: 0 0 8px;
}
.empty-sub {
  font-size: 0.8rem; color: #94A3B8;
  max-width: 320px; line-height: 1.6; margin: 0 0 20px;
}
.empty-refresh-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: white; border: 1.5px solid #E2E8F0;
  border-radius: 10px; padding: 9px 18px;
  font-size: 0.78rem; font-weight: 700; color: #1E4276;
  cursor: pointer; transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(30,66,118,0.07);
}
.empty-refresh-btn:hover {
  border-color: #00AEEF; color: #00AEEF;
  box-shadow: 0 4px 14px rgba(0,174,239,0.12);
}

/* ── RESPONSIVE ── */
@media (max-width: 600px) {
  .notif-shell { padding: 14px 14px 40px; }
  .notif-header-band { flex-direction: column; align-items: flex-start; gap: 12px; }
  .filter-row { gap: 6px; }
}
</style>
